// Shared constants + API helpers for the Dispatcher screens.
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ShieldAlert, Clock, CheckCircle2, XCircle, Truck, Users, HandCoins, Map as MapIcon, BarChart3, Calendar, Heart } from "lucide-react";
import { API } from "@/lib/api";
export { API };

// Fix default marker icon paths (Leaflet's Webpack quirk)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export const driverIcon = new L.DivIcon({
  html: '<div style="background:#0d9488;color:#fff;padding:6px 10px;border-radius:9999px;font-size:12px;font-weight:600;box-shadow:0 2px 8px rgba(0,0,0,0.25);white-space:nowrap;border:2px solid white;">🚐</div>',
  className: "",
  iconSize: [40, 40],
  iconAnchor: [20, 20],
});

export const AUTH_KEY = "dispatcher_auth";

export const STATUS_TABS = [
  { key: "held", label: "Held (action needed)", icon: ShieldAlert, tone: "bg-amber-100 text-amber-800 border-amber-300" },
  { key: "pending_payment", label: "Pending payment", icon: Clock, tone: "bg-blue-100 text-blue-800 border-blue-300" },
  { key: "cash_pickups", label: "Cash pickups", icon: HandCoins, tone: "bg-amber-100 text-amber-800 border-amber-400" },
  { key: "insurance_pending", label: "Insurance pending", icon: Clock, tone: "bg-purple-100 text-purple-800 border-purple-300" },
  { key: "completed", label: "Completed", icon: CheckCircle2, tone: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { key: "cancelled", label: "Cancelled", icon: XCircle, tone: "bg-slate-100 text-slate-700 border-slate-300" },
  { key: "drivers", label: "Drivers", icon: Truck, tone: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  { key: "patients", label: "Patients & facilities", icon: Users, tone: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300" },
  { key: "calendar", label: "Calendar", icon: Calendar, tone: "bg-purple-100 text-purple-800 border-purple-300" },
  { key: "map", label: "Live map", icon: MapIcon, tone: "bg-teal-100 text-teal-800 border-teal-300" },
  { key: "homecare_sweep", label: "Home Care route", icon: Heart, tone: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { key: "insights", label: "Insights", icon: BarChart3, tone: "bg-rose-100 text-rose-800 border-rose-300" },
];

export const money = (cents) => (cents ? `$${(cents / 100).toFixed(2)}` : "—");
export const titleCase = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export async function fetchRides(auth, status) {
  const url = `${API}/nemt/rides${status ? `?status=${status}` : ""}`;
  const res = await fetch(url, { headers: { "X-Dispatcher-Auth": auth } });
  if (res.status === 401) throw new Error("UNAUTHORIZED");
  if (!res.ok) throw new Error(`Server ${res.status}`);
  return res.json();
}

export async function fetchDrivers(auth) {
  const res = await fetch(`${API}/nemt/admin/drivers`, { headers: { "X-Dispatcher-Auth": auth } });
  if (res.status === 401) throw new Error("UNAUTHORIZED");
  if (!res.ok) throw new Error(`Server ${res.status}`);
  return res.json();
}

export async function postAction(auth, rideId, action, body = {}) {
  const res = await fetch(`${API}/nemt/rides/${rideId}/${action}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Dispatcher-Auth": auth },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Server ${res.status}`);
  return data;
}

export async function adminPost(auth, path, body = {}) {
  const res = await fetch(`${API}/nemt/admin/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Dispatcher-Auth": auth },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.detail || `Server ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function adminGet(auth, path) {
  const res = await fetch(`${API}/nemt/admin/${path}`, {
    headers: { "X-Dispatcher-Auth": auth },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.detail || `Server ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

export async function adminDelete(auth, path) {
  const res = await fetch(`${API}/nemt/admin/${path}`, {
    method: "DELETE",
    headers: { "X-Dispatcher-Auth": auth },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.detail || `Server ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

export async function tryAssignWithOverride(auth, rideId, driverId) {
  try {
    return await adminPost(auth, `rides/${rideId}/assign`, { driver_id: driverId });
  } catch (err) {
    if (err.status === 409) {
      const ok = window.confirm(
        `${err.message}\n\nOVERRIDE this concurrent-ride guard and assign anyway?\n(Recorded on the ride's audit trail.)`
      );
      if (ok) {
        return await adminPost(auth, `rides/${rideId}/assign`, {
          driver_id: driverId,
          override_conflict: true,
        });
      }
    }
    throw err;
  }
}
